import { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useSettingsStore } from '../../hooks';
import { Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, useToast } from '@lancar-pos/ui';

interface ServiceAccountCredentials {
  type: string;
  project_id: string;
  private_key_id: string;
  private_key: string;
  client_email: string;
  client_id: string;
  auth_uri: string;
  token_uri: string;
  auth_provider_x509_cert_url: string;
  client_x509_cert_url: string;
}

export function GoogleSheetsSetup() {
  const [jsonInput, setJsonInput] = useState('');
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  const { setSetting, getSetting } = useSettingsStore();
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const savedCredentials = getSetting('sheets_credentials') as string;
    const savedSpreadsheetId = getSetting('sheets_spreadsheet_id') as string;

    if (savedCredentials) setJsonInput(savedCredentials);
    if (savedSpreadsheetId) setSpreadsheetId(savedSpreadsheetId);
  }, []);

  const parseCredentials = (json: string): ServiceAccountCredentials | null => {
    try {
      const creds = JSON.parse(json);
      if (!creds.client_email || !creds.private_key) {
        return null;
      }
      return creds;
    } catch {
      return null;
    }
  };

  const handleTestConnection = async () => {
    if (!jsonInput.trim()) {
      toast.error('Masukkan JSON credentials');
      return;
    }

    if (!spreadsheetId.trim()) {
      toast.error('Masukkan Spreadsheet ID');
      return;
    }

    const creds = parseCredentials(jsonInput);
    if (!creds) {
      toast.error('Format JSON tidak valid');
      return;
    }

    setTesting(true);
    try {
      let accessToken: string;
      try {
        accessToken = await getAccessToken(creds);
      } catch (authError) {
        toast.error('Autentikasi gagal', authError instanceof Error ? authError.message : 'Periksa kredensial service account');
        setTesting(false);
        return;
      }

      const response = await fetch(
        `https://sheets.googleapis.com/v4/spreadsheets/${spreadsheetId}/values/Products!A1:A1`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (response.ok) {
        toast.success('Koneksi berhasil!', 'Google Sheets terhubung');
      } else if (response.status === 403) {
        toast.error('Akses ditolak', 'Pastikan spreadsheet sudah di-share ke service account');
      } else if (response.status === 404) {
        toast.error('Spreadsheet tidak ditemukan', 'Periksa kembali Spreadsheet ID');
      } else {
        toast.error('Gagal menghubungkan', `Error: ${response.status}`);
      }
    } catch (error) {
      toast.error('Gagal menghubungkan', 'Periksa koneksi internet');
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!jsonInput.trim()) {
      toast.error('Masukkan JSON credentials');
      return;
    }

    if (!spreadsheetId.trim()) {
      toast.error('Masukkan Spreadsheet ID');
      return;
    }

    const creds = parseCredentials(jsonInput);
    if (!creds) {
      toast.error('Format JSON tidak valid');
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.all([
        setSetting('sheets_credentials', jsonInput),
        setSetting('sheets_spreadsheet_id', spreadsheetId),
        setSetting('sheets_configured', true),
        setSetting('sheets_client_email', creds.client_email),
        setSetting('onboarding_completed', true),
      ]);

      const allSuccess = results.every(r => r.success);

      if (allSuccess) {
        toast.success('Berhasil disimpan', 'Kredensial Google Sheets tersimpan');
        navigate({ to: '/app', replace: true });
      } else {
        toast.error('Gagal menyimpan beberapa pengaturan');
      }
    } catch (error) {
      toast.error('Gagal menyimpan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
      <Card className="w-full max-w-2xl">
        <CardHeader>
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                <path d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
          </div>
          <CardTitle className="text-center">Setup Google Sheets</CardTitle>
          <CardDescription className="text-center">
            Hubungkan LancarPOS ke Google Sheets untuk sinkronisasi data
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Langkah-langkah:</h4>
            <ol className="text-sm text-blue-700 space-y-1 list-decimal list-inside">
              <li>Buat project di Google Cloud Console</li>
              <li>Aktifkan Google Sheets API & Google Drive API</li>
              <li>Buat Service Account dan download JSON key</li>
              <li>Buat spreadsheet baru atau gunakan yang ada</li>
              <li>Share spreadsheet ke email service account (Editor)</li>
              <li>Copy-paste JSON key di bawah ini</li>
            </ol>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <h4 className="font-medium text-amber-900 mb-2">Batasan Google Sheets:</h4>
            <ul className="text-sm text-amber-700 space-y-1 list-disc list-inside">
              <li>Maksimal 10 juta sel per spreadsheet</li>
              <li>Maksimal 26 kolom per sheet (A-Z)</li>
              <li>Untuk volume tinggi, pertimbangkan untuk mengarsip data lama secara berkala</li>
            </ul>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Spreadsheet ID
            </label>
            <input
              type="text"
              value={spreadsheetId}
              onChange={(e) => setSpreadsheetId(e.target.value)}
              placeholder="1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgvE2upms"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
            <p className="text-xs text-gray-500 mt-1">
              Dari URL: https://docs.google.com/spreadsheets/d/<strong>SPREADSHEET_ID</strong>/edit
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Service Account JSON
            </label>
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
              placeholder='{"type": "service_account", "project_id": "...", ...}'
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-sm"
            />
          </div>
        </CardContent>

        <CardFooter className="flex gap-3">
          <Button variant="outline" onClick={() => navigate({ to: '/app/settings' })}>
            Kembali
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={handleTestConnection} loading={testing}>
            Test Koneksi
          </Button>
          <Button onClick={handleSave} loading={loading}>
            Simpan
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}

async function getAccessToken(creds: ServiceAccountCredentials): Promise<string> {
  const base64url = (str: string) => {
    return btoa(str)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  };

  const header = base64url(JSON.stringify({ alg: 'RS256', typ: 'JWT' }));

  const now = Math.floor(Date.now() / 1000);
  const claim = base64url(JSON.stringify({
    iss: creds.client_email,
    scope: 'https://www.googleapis.com/auth/spreadsheets',
    aud: 'https://oauth2.googleapis.com/token',
    exp: now + 3600,
    iat: now,
  }));

  const signatureInput = `${header}.${claim}`;

  const privateKeyPem = creds.private_key
    .replace(/-----BEGIN PRIVATE KEY-----/g, '')
    .replace(/-----END PRIVATE KEY-----/g, '')
    .replace(/\s/g, '');

  const binaryKey = Uint8Array.from(atob(privateKeyPem), c => c.charCodeAt(0));

  const key = await crypto.subtle.importKey(
    'pkcs8',
    binaryKey.buffer,
    { name: 'RSASSA-PKCS1-v1_5', hash: 'SHA-256' },
    false,
    ['sign']
  );

  const encoder = new TextEncoder();
  const signatureBuffer = await crypto.subtle.sign(
    'RSASSA-PKCS1-v1_5',
    key,
    encoder.encode(signatureInput)
  );

  const signatureArray = new Uint8Array(signatureBuffer);
  const signature = btoa(String.fromCharCode(...signatureArray))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

  const jwt = `${signatureInput}.${signature}`;

  const response = await fetch('https://oauth2.googleapis.com/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: `grant_type=urn%3Aietf%3Aparams%3Aoauth%3Agrant-type%3Ajwt-bearer&assertion=${jwt}`,
  });

  const data = await response.json();

  if (!response.ok) {
    throw new Error(data.error_description || data.error || 'Failed to get access token');
  }

  return data.access_token;
}
